import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, Play, Power, PowerOff, ScrollText, Workflow } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchWorkflows,
  toggleWorkflow,
  runWorkflow,
  AUTO_STR as S,
  type WorkflowRow,
} from './automationApi'

/**
 * A26.01 — Сценарии автоматизации (workflows) + A26.04 run-now. Enable/disable
 * or trigger a run. Dormant until `/admin/automation/workflows` ships.
 * Placeholder permission USERS_VIEW.
 */
export function WorkflowsScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<WorkflowRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'trigger', header: S.trigger, type: 'text' },
      { key: 'actions', header: S.actions, type: 'number' },
      { key: 'enabled', header: S.enabled, type: 'text' },
      { key: 'last_run', header: S.lastRun, type: 'text' },
      { key: 'runs', header: S.runs, type: 'number' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<WorkflowRow>[]>(
    () => [
      {
        id: 'run',
        label: S.run,
        icon: <Play size={14} />,
        onSelect: async (r) => {
          try {
            await runWorkflow(r.id)
            toast(S.started, { tone: 'success' })
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
      {
        id: 'disable',
        label: S.disable,
        icon: <PowerOff size={14} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: async (r) => {
          try {
            await toggleWorkflow(r.id, false)
            toast(S.toggled, { tone: 'success' })
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
      {
        id: 'enable',
        label: S.enable,
        icon: <Power size={14} />,
        visible: (r) => !r.enabled,
        onSelect: async (r) => {
          try {
            await toggleWorkflow(r.id, true)
            toast(S.toggled, { tone: 'success' })
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchWorkflows, [])
  return (
    <>
      <PageHeader
        title={S.workflows}
        screenId="A26.01"
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/automation/scheduled"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <CalendarClock size={13} />
              {S.scheduled}
            </Link>
            <Link
              to="/automation/executions"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ScrollText size={13} />
              {S.executions}
            </Link>
          </div>
        }
      />
      <DataTable<WorkflowRow>
        screenId="A26.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Workflow size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
